/**
 * Cast Button Component
 *
 * Provides casting functionality for media to Smart TVs
 * Supports Google Cast (Chromecast) and AirPlay (Safari)
 */

import React, { useState, useEffect } from 'react';
import { castService } from '../services/casting';
import './CastButton.css';

function CastButton({ mediaUrl, mediaType = 'audio', metadata = {} }) {
  const [isCastAvailable, setIsCastAvailable] = useState(false);
  const [isAirPlayAvailable, setIsAirPlayAvailable] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [showDeviceList, setShowDeviceList] = useState(false);

  // Check casting availability on mount
  useEffect(() => {
    let isMounted = true;

    const checkAvailability = async () => {
      try {
        console.log('🔍 CastButton: Checking casting availability...');

        // Check AirPlay (Safari only) - this is always available if Safari
        const airPlayAvailable = castService.isAirPlayAvailable();
        if (isMounted) {
          setIsAirPlayAvailable(airPlayAvailable);
        }
        console.log('📱 CastButton: AirPlay available:', airPlayAvailable);

        // Check if we're in a Chromium browser
        const isChromium = /Chrome|Chromium|Edge/.test(navigator.userAgent);
        console.log('🌐 CastButton: Browser is Chromium:', isChromium);

        // Try to load and initialize Cast SDK
        try {
          // First, try to load the Cast SDK if not already loaded
          if (!window.cast && !window.chrome?.cast) {
            console.log('📥 CastButton: Loading Cast SDK...');
            try {
              await castService.loadCastSDK();
              console.log('✅ CastButton: Cast SDK loaded');
            } catch (loadError) {
              console.warn(
                '⚠️ CastButton: Failed to load Cast SDK:',
                loadError
              );
              // Continue anyway - might work on click
            }
          }

          // Wait a bit for SDK to initialize
          await new Promise((resolve) => setTimeout(resolve, 1000));

          if (!isMounted) return;

          // Check if Cast is now available
          const castAvailable = castService.isCastAvailable();
          console.log('✅ CastButton: Cast available:', castAvailable);
          if (isMounted) {
            setIsCastAvailable(castAvailable);
          }

          // Initialize Cast if available
          if (castAvailable) {
            try {
              console.log('🚀 CastButton: Initializing Cast...');
              await castService.initializeCast();
              const devices = await castService.getAvailableDevices();
              if (isMounted) {
                setAvailableDevices(devices);
              }
              console.log('📺 CastButton: Available devices:', devices.length);

              // Listen for cast session changes
              if (isMounted) {
                castService.onSessionChange((event) => {
                  if (isMounted) {
                    setIsCasting(
                      event.sessionState ===
                        window.cast?.framework?.SessionState?.SESSION_STARTED
                    );
                  }
                });
              }
            } catch (initError) {
              console.warn(
                '⚠️ CastButton: Cast initialization failed:',
                initError
              );
              // Still show button - user can try to cast
              if (isMounted && isChromium) {
                setIsCastAvailable(true);
              }
            }
          } else if (isChromium) {
            // Show button even if Cast not immediately available
            // It might become available when user clicks
            console.log(
              '💡 CastButton: Showing button for Chromium browser (Cast may become available)'
            );
            if (isMounted) {
              setIsCastAvailable(true);
            }
          }
        } catch (error) {
          console.warn(
            '⚠️ CastButton: Error checking cast availability:',
            error
          );
          // Check if we're in a Chromium browser - show button anyway
          if (isMounted && isChromium) {
            console.log(
              '💡 CastButton: Showing button for Chromium browser despite error'
            );
            setIsCastAvailable(true);
          }
        }
      } catch (error) {
        console.error('❌ CastButton: Unexpected error:', error);
        // Don't break the component - just log the error
      }
    };

    checkAvailability().catch((error) => {
      console.error(
        '❌ CastButton: Unhandled error in checkAvailability:',
        error
      );
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle cast button click
  const handleCastClick = async () => {
    try {
      if (isCasting) {
        // Stop casting
        try {
          await castService.stopCasting();
          setIsCasting(false);
        } catch (error) {
          console.error('Error stopping cast:', error);
          setIsCasting(false); // Update state anyway
        }
      } else {
        // Try to initialize Cast if not already done
        try {
          // Check if Cast is available first
          if (!castService.isCastAvailable()) {
            // Try loading SDK
            try {
              await castService.loadCastSDK();
              await new Promise((resolve) => setTimeout(resolve, 500));
            } catch (loadError) {
              console.warn('Failed to load Cast SDK:', loadError);
              // Continue - might still work
            }
          }

          const initialized = await castService.initializeCast();
          if (!initialized) {
            alert(
              'Chromecast is not available. Make sure you are using Chrome or Edge and have a Chromecast device on the same network.'
            );
            return;
          }

          // Get available devices
          const devices = await castService.getAvailableDevices();
          setAvailableDevices(devices);

          // Show device list or start casting
          if (devices.length > 0) {
            setShowDeviceList(!showDeviceList);
          } else {
            // Start casting directly - Cast SDK will show device picker
            try {
              if (mediaType === 'audio') {
                await castService.castAudioToChromecast(mediaUrl, metadata);
              } else {
                await castService.castToChromecast(mediaUrl, metadata);
              }
              setIsCasting(true);
            } catch (error) {
              console.error('Error starting cast:', error);
              alert(
                `Failed to cast: ${
                  error.message || 'Unknown error'
                }\n\nMake sure you have a Chromecast device on the same network.`
              );
            }
          }
        } catch (error) {
          console.error('Error initializing cast:', error);
          alert(
            `Cast not available: ${
              error.message || 'Unknown error'
            }\n\nMake sure you are using Chrome or Edge and have a Chromecast device on the same network.`
          );
        }
      }
    } catch (error) {
      console.error('Unexpected error in handleCastClick:', error);
      alert('An unexpected error occurred. Please try again.');
    }
  };

  // Handle AirPlay (native Safari support)
  const handleAirPlayClick = () => {
    // AirPlay is handled natively by the video/audio element
    // This button just indicates availability
    alert(
      'AirPlay is available! Use the AirPlay button in the video/audio player controls.'
    );
  };

  // Show button if:
  // 1. Cast is available, OR
  // 2. AirPlay is available, OR
  // 3. We're in a Chromium browser (Cast might become available)
  const isChromium = /Chrome|Chromium|Edge/.test(navigator.userAgent);
  const shouldShow = isCastAvailable || isAirPlayAvailable || isChromium;

  if (!shouldShow) {
    return null; // Don't show button if casting not available
  }

  return (
    <div className="cast-button-container">
      {isCastAvailable && (
        <button
          className={`cast-button ${isCasting ? 'casting' : ''}`}
          onClick={handleCastClick}
          title={isCasting ? 'Stop Casting' : 'Cast to Chromecast'}
        >
          {isCasting ? (
            <>
              <span className="cast-icon">📺</span>
              <span className="cast-label">Stop Cast</span>
            </>
          ) : (
            <>
              <span className="cast-icon">📺</span>
              <span className="cast-label">Cast</span>
            </>
          )}
        </button>
      )}

      {isAirPlayAvailable && (
        <button
          className="cast-button airplay"
          onClick={handleAirPlayClick}
          title="AirPlay Available (use player controls)"
        >
          <span className="cast-icon">📱</span>
          <span className="cast-label">AirPlay</span>
        </button>
      )}

      {showDeviceList && availableDevices.length > 0 && (
        <div className="cast-device-list">
          {availableDevices.map((device) => (
            <button
              key={device.id}
              className="cast-device-item"
              onClick={async () => {
                try {
                  if (mediaType === 'audio') {
                    await castService.castAudioToChromecast(mediaUrl, metadata);
                  } else {
                    await castService.castToChromecast(mediaUrl, metadata);
                  }
                  setIsCasting(true);
                  setShowDeviceList(false);
                } catch (error) {
                  console.error('Error casting to device:', error);
                  alert(`Failed to cast: ${error.message || 'Unknown error'}`);
                }
              }}
            >
              {device.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CastButton;
